import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { isApiClientError } from "../api/errors";
import {
  classifySigningFailure,
  type DocumentWorkflowFileVersion,
  type DocumentWorkflowSignature,
  type DocumentWorkflowSignatureVerification,
} from "../entities/document-workflow";
import { useDocumentFiles } from "./documentFilesContext";
import { saveEncodedFile, saveSignedFile } from "./versionDownload";
import { SignatureDetails } from "./SignatureDetails";
import { failureMessages } from "./signingMessages";
import { Notice } from "./Notice";

function signaturesError(error: unknown): string {
  if (isApiClientError(error) && error.status === 403) {
    return "Подписи недоступны: нет права на файлы документа.";
  }

  return "Не удалось загрузить подписи. Повторите попытку.";
}

/** Итог повторной проверки: неверная подпись — не сбой сервера и не успех. */
function VerificationResult({
  result,
}: {
  result: DocumentWorkflowSignatureVerification;
}) {
  return result.status === "verified" ? (
    <p role="status" data-verification="verified">
      Повторная проверка пройдена: подпись верна.
    </p>
  ) : (
    <p role="alert" data-verification="invalid">
      Подпись не прошла повторную проверку
      {result.error_code ? ` (код: ${result.error_code})` : ""}. Она не
      считается верной.
    </p>
  );
}

function SignatureCard({
  signature,
  versionId,
}: {
  signature: DocumentWorkflowSignature;
  versionId: string;
}) {
  const { services } = useDocumentFiles();
  const client = useQueryClient();
  const verify = useMutation({
    mutationFn: () => services.documentWorkflow.verifySignature(signature.id),
    onSuccess: () =>
      client.invalidateQueries({
        queryKey: ["document-workflow", "versions", versionId, "signatures"],
      }),
  });
  const failure = verify.isError ? classifySigningFailure(verify.error) : null;
  // Подпись отделённая: получателю нужен её файл рядом с самим документом,
  // иначе проверить подпись у себя он не сможет.
  const save = useMutation({
    mutationFn: async () => {
      const file = await services.documentWorkflow.signatureFile(signature.id);
      saveEncodedFile(file.content, file.filename, file.mime_type);
    },
  });

  return (
    <li className="signature-card">
      <SignatureDetails signature={signature} />
      <div className="inline-actions">
        <button
          className="button button--secondary button--compact"
          type="button"
          disabled={verify.isPending}
          onClick={() => verify.mutate()}
        >
          {verify.isPending ? "Проверяем…" : "Проверить повторно"}
        </button>
        <button
          className="button button--secondary button--compact"
          type="button"
          disabled={save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? "Готовим…" : "Скачать подпись"}
        </button>
      </div>
      {save.isError ? (
        <Notice>Не удалось получить файл подписи. Повторите попытку.</Notice>
      ) : null}
      {verify.data ? <VerificationResult result={verify.data} /> : null}
      {failure ? (
        <div role="alert" data-failure={failure}>
          <p>
            <strong>
              {
                failureMessages[
                  failure === "verifier_unavailable" ||
                  failure === "permission_denied"
                    ? failure
                    : "failed"
                ].title
              }
            </strong>
          </p>
          <p>
            {failure === "verifier_unavailable" ||
            failure === "permission_denied"
              ? failureMessages[failure].text
              : "Повторная проверка не выполнена. Повторите попытку."}
          </p>
        </div>
      ) : null}
    </li>
  );
}

/** Копия собирается в фоне, поэтому её ждут, переспрашивая состояние. */
async function buildStampedCopy(
  services: ReturnType<typeof useDocumentFiles>["services"],
  versionId: string,
): Promise<void> {
  let state = await services.documentWorkflow.requestSignatureStamp(versionId);
  for (
    let attempt = 0;
    state.status !== "completed" && attempt < 40;
    attempt++
  ) {
    if (state.status === "failed") {
      throw new StampFailedError("Сервер не смог собрать печатную копию.");
    }
    await new Promise((resolve) => setTimeout(resolve, 1500));
    state = await services.documentWorkflow.signatureStamp(state.id);
  }
  if (state.status !== "completed") {
    throw new StampFailedError("Копия ещё готовится. Повторите попытку.");
  }
  const signed = await services.documentWorkflow.downloadSignatureStamp(
    state.id,
  );
  saveSignedFile(signed.url, "документ-с-отметкой.pdf");
}

/** Сборка печатной копии не удалась. */
class StampFailedError extends Error {
  readonly name = "StampFailedError";
}

function stampError(error: unknown): string {
  if (error instanceof StampFailedError) return error.message;
  if (isApiClientError(error) && error.status === 403) {
    return "Недостаточно прав на файлы документа.";
  }

  return "Не удалось получить печатную копию. Повторите попытку.";
}

/** Подписи версии и их повторная проверка. */
export function VersionSignatures({
  version,
}: {
  version: DocumentWorkflowFileVersion;
}) {
  const { services } = useDocumentFiles();
  const signatures = useQuery({
    queryKey: ["document-workflow", "versions", version.id, "signatures"],
    queryFn: () => services.documentWorkflow.versionSignatures(version.id),
    retry: false,
  });
  const stamp = useMutation({
    mutationFn: () => buildStampedCopy(services, version.id),
  });

  return (
    <section
      className="version-signatures"
      aria-label={`Подписи версии ${version.version_number}`}
    >
      {signatures.isPending ? <p aria-busy="true">Загружаем подписи…</p> : null}
      {signatures.isError ? (
        <Notice>{signaturesError(signatures.error)}</Notice>
      ) : null}
      {signatures.data?.length === 0 ? (
        <p className="dw-secondary">У этой версии пока нет подписей.</p>
      ) : null}
      {signatures.data?.length ? (
        <div className="inline-actions">
          <button
            className="button button--secondary button--compact"
            type="button"
            disabled={stamp.isPending}
            onClick={() => stamp.mutate()}
          >
            {stamp.isPending ? "Собираем копию…" : "Печатная копия с отметкой"}
          </button>
        </div>
      ) : null}
      {stamp.isError ? <Notice>{stampError(stamp.error)}</Notice> : null}
      {signatures.data?.length ? (
        <ul className="signature-list">
          {signatures.data.map((signature) => (
            <SignatureCard
              key={signature.id}
              signature={signature}
              versionId={version.id}
            />
          ))}
        </ul>
      ) : null}
    </section>
  );
}
